import React from 'react'
  import { MDXTag } from '@mdx-js/tag'


  const layoutProps = {};
export default class MDXContent extends React.Component {
  constructor(props) {
    super(props);
    this.layout = null;
  }

  render() {
    const {
      components,
      ...props
    } = this.props;
    return <MDXTag name="wrapper" components={components}><MDXTag name="h1" components={components}>{`Theming`}</MDXTag>
      <MDXTag name="p" components={components}>{`To apply themes to Rebass components, add a `}<MDXTag name="a" components={components} parentName="p" props={{
          "href": "https://www.styled-components.com/docs/advanced#theming"
        }}>{`ThemeProvider`}</MDXTag>{` component to the root of your application and pass a `}<MDXTag name="inlineCode" components={components} parentName="p">{`theme`}</MDXTag>{` object as a prop.`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`import React from 'react'
import { ThemeProvider } from 'styled-components'
import theme from './theme'

export default props =>
  <ThemeProvider theme={theme}>
    {props.children}
  </ThemeProvider>
`}</MDXTag></MDXTag>
      <MDXTag name="p" components={components}>{`If you do not apply your own theme, or you don't include all the keys the theme object expects, Rebass will revert to the default values, which are inherited from `}<MDXTag name="a" components={components} parentName="p" props={{
          "href": "https://github.com/jxnblk/styled-system"
        }}>{`styled-system`}</MDXTag>{`.`}</MDXTag>
      <MDXTag name="p" components={components}>{`An example theme could look like the following:`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-js"
        }}>{`// example theme.js

export default {
  breakpoints: ['40em', '52em', '64em'],
  fontSizes: [
    12, 14, 16, 20, 24, 32, 48, 64
  ],
  colors: {
    blue: '#07c',
    lightgray: '#f6f6ff'
  },
  space: [
    0, 4, 8, 16, 32, 64, 128, 256
  ],
  fonts: {
    sans: 'system-ui, sans-serif',
    mono: 'Menlo, monospace',
  },
  shadows: {
    small: '0 0 4px rgba(0, 0, 0, .125)',
    large: '0 0 24px rgba(0, 0, 0, .125)'
  }
}
`}</MDXTag></MDXTag>
      <MDXTag name="h2" components={components}>{`Theme Object`}</MDXTag>
      <MDXTag name="p" components={components}>{`Rebass and `}<MDXTag name="a" components={components} parentName="p" props={{
          "href": "https://github.com/jxnblk/styled-system"
        }}>{`styled-system`}</MDXTag>{` allow global font sizes, colors, spacing, button variants, and other values to be set with a theme object.`}</MDXTag>
      <MDXTag name="p" components={components}>{`The following keys will be picked up by styled-system props:`}</MDXTag>
      <MDXTag name="table" components={components}>
        <MDXTag name="thead" components={components} parentName="table">
          <MDXTag name="tr" components={components} parentName="thead">
            <MDXTag name="th" components={components} parentName="tr" props={{
              "align": null
            }}>{`Key`}</MDXTag>
            <MDXTag name="th" components={components} parentName="tr" props={{
              "align": null
            }}>{`Type`}</MDXTag>
            <MDXTag name="th" components={components} parentName="tr" props={{
              "align": null
            }}>{`Description`}</MDXTag>
          </MDXTag>
        </MDXTag>
        <MDXTag name="tbody" components={components} parentName="table">
          <MDXTag name="tr" components={components} parentName="tbody">
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}><MDXTag name="inlineCode" components={components} parentName="td">{`breakpoints`}</MDXTag></MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Array`}</MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Array of strings representing viewport widths to use for min-width media queries.`}</MDXTag>
          </MDXTag>
          <MDXTag name="tr" components={components} parentName="tbody">
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}><MDXTag name="inlineCode" components={components} parentName="td">{`fontSizes`}</MDXTag></MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Array`}</MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Array of numbers to use as a typographic scale`}</MDXTag>
          </MDXTag>
          <MDXTag name="tr" components={components} parentName="tbody">
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}><MDXTag name="inlineCode" components={components} parentName="td">{`colors`}</MDXTag></MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Object`}</MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Color names that can be used in `}<MDXTag name="inlineCode" components={components} parentName="td">{`color`}</MDXTag>{`, `}<MDXTag name="inlineCode" components={components} parentName="td">{`bg`}</MDXTag>{`, and `}<MDXTag name="inlineCode" components={components} parentName="td">{`borderColor`}</MDXTag>{` props`}</MDXTag>
          </MDXTag>
          <MDXTag name="tr" components={components} parentName="tbody">
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}><MDXTag name="inlineCode" components={components} parentName="td">{`space`}</MDXTag></MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Array`}</MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Array of numbers for use as margin and pixel values`}</MDXTag>
          </MDXTag>
          <MDXTag name="tr" components={components} parentName="tbody">
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}><MDXTag name="inlineCode" components={components} parentName="td">{`fonts`}</MDXTag></MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Array or Object`}</MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Values for the `}<MDXTag name="inlineCode" components={components} parentName="td">{`fontFamily`}</MDXTag>{` prop`}</MDXTag>
          </MDXTag>
          <MDXTag name="tr" components={components} parentName="tbody">
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}><MDXTag name="inlineCode" components={components} parentName="td">{`fontWeights`}</MDXTag></MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Array or Object`}</MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Values for `}<MDXTag name="inlineCode" components={components} parentName="td">{`fontWeight`}</MDXTag>{` prop`}</MDXTag>
          </MDXTag>
          <MDXTag name="tr" components={components} parentName="tbody">
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}><MDXTag name="inlineCode" components={components} parentName="td">{`lineHeights`}</MDXTag></MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Array or Object`}</MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Values for `}<MDXTag name="inlineCode" components={components} parentName="td">{`lineHeight`}</MDXTag>{` prop`}</MDXTag>
          </MDXTag>
          <MDXTag name="tr" components={components} parentName="tbody">
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}><MDXTag name="inlineCode" components={components} parentName="td">{`letterSpacings`}</MDXTag></MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Array or Object`}</MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Values for `}<MDXTag name="inlineCode" components={components} parentName="td">{`letterSpacing`}</MDXTag>{` prop`}</MDXTag>
          </MDXTag>
          <MDXTag name="tr" components={components} parentName="tbody">
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}><MDXTag name="inlineCode" components={components} parentName="td">{`shadows`}</MDXTag></MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Array or Object`}</MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Values for `}<MDXTag name="inlineCode" components={components} parentName="td">{`boxShadow`}</MDXTag>{` prop`}</MDXTag>
          </MDXTag>
          <MDXTag name="tr" components={components} parentName="tbody">
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}><MDXTag name="inlineCode" components={components} parentName="td">{`borders`}</MDXTag></MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Array or Object`}</MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Values for `}<MDXTag name="inlineCode" components={components} parentName="td">{`border`}</MDXTag>{` props`}</MDXTag>
          </MDXTag>
          <MDXTag name="tr" components={components} parentName="tbody">
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}><MDXTag name="inlineCode" components={components} parentName="td">{`radii`}</MDXTag></MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Array or Object`}</MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Values for `}<MDXTag name="inlineCode" components={components} parentName="td">{`borderRadius`}</MDXTag>{` props`}</MDXTag>
          </MDXTag>
          <MDXTag name="tr" components={components} parentName="tbody">
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}><MDXTag name="inlineCode" components={components} parentName="td">{`opacity`}</MDXTag></MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Array or Object`}</MDXTag>
            <MDXTag name="td" components={components} parentName="tr" props={{
              "align": null
            }}>{`Values for `}<MDXTag name="inlineCode" components={components} parentName="td">{`opacity`}</MDXTag>{` props`}</MDXTag>
          </MDXTag>
        </MDXTag>
      </MDXTag>
      <MDXTag name="p" components={components}>{`By default arrays of numbers are interpreted as `}<MDXTag name="inlineCode" components={components} parentName="p">{`px`}</MDXTag>{`, other unit of measurements can be specified by using an array of strings instead, e.x. `}<MDXTag name="inlineCode" components={components} parentName="p">{`['768px', '992px', '1200px']`}</MDXTag>{`.`}</MDXTag>
      <MDXTag name="h2" components={components}>{`Button & Card Variants`}</MDXTag>
      <MDXTag name="p" components={components}>{`The Button and Card components accept a `}<MDXTag name="inlineCode" components={components} parentName="p">{`variant`}</MDXTag>{` prop to pick up predefined styles in the theme.`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-js"
        }}>{`// example theme.js
const blue = '#07c'

export default {
  buttons: {
    primary: {
      color: '#fff',
      backgroundColor: blue,
    },
    outline: {
      color: blue,
      backgroundColor: 'transparent',
      boxShadow: 'inset 0 0 0 2px'
    }
  }
}
`}</MDXTag></MDXTag>
      <MDXTag name="p" components={components}>{`With the above `}<MDXTag name="inlineCode" components={components} parentName="p">{`buttons`}</MDXTag>{` object, the Button component can apply styles based on the `}<MDXTag name="inlineCode" components={components} parentName="p">{`variant`}</MDXTag>{` prop.`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`<Button variant='primary' />
<Button variant='outline' />
`}</MDXTag></MDXTag>
           </MDXTag>;
  }

}
MDXContent.isMDXComponent = true;
export const _frontmatter = {};
      